/**
 * Feature Access Hook
 *
 * Resolves whether the current user has access to a given feature, based on:
 *   1. Their subscription tier  (starter / growth / pro_solo / studio / legacy)
 *   2. Their active add-ons     (from `subscription_addons`)
 *
 * Some features are gated by tier only, some by add-on only, and some require
 * BOTH (e.g. the white-label app store requires Studio tier AND the add-on).
 *
 * The `FEATURE_GATES` map below is the single source of truth for gating
 * logic on the frontend. Keep it in sync with the server-side
 * `sync_features_for_tier()` function and Stripe product configuration.
 */

import { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SubscriptionTier =
  // New pricing tiers
  | "starter"
  | "growth"
  | "pro"
  | "studio"
  // Legacy tiers (still present in DB; kept for backwards compatibility)
  | "free"
  | "pro"
  | "pro_plus"
  | "elite"
  | "team_pro"
  | "team_pro_plus"
  | "team_elite";

export type AddonKey =
  | "route_optimization"
  | "advanced_automations"
  | "whitelabel_app_store"
  | "marketplace_listing";

export interface ActiveAddon {
  addon_key: AddonKey;
  activated_at: string;
  price_cents: number;
}

interface FeatureGateDefinition {
  /** Tiers that grant this feature. */
  tier?: SubscriptionTier[];
  /** Addon key that grants this feature. */
  addon?: AddonKey;
  /** If true, BOTH tier and addon are required. Otherwise either is sufficient. */
  both?: boolean;
}

// ---------------------------------------------------------------------------
// Feature gates — single source of truth for frontend gating.
// ---------------------------------------------------------------------------

/**
 * The list of tiers that grant access to ALL tiers (i.e. every paid plan).
 * Legacy tiers are included so pre-migration accounts keep working.
 */
const ALL_TIERS: SubscriptionTier[] = [
  "starter",
  "growth",
  "pro",
  "studio",
  "free",
  "pro",
  "pro_plus",
  "elite",
  "team_pro",
  "team_pro_plus",
  "team_elite",
];

/**
 * Growth+ (Growth, Pro, Studio) — includes the legacy pro_plus/elite/team_* tiers
 * so existing subscribers aren't downgraded.
 */
const GROWTH_PLUS: SubscriptionTier[] = [
  "growth",
  "pro",
  "studio",
  // Legacy equivalents
  "pro",
  "pro_plus",
  "elite",
  "team_pro",
  "team_pro_plus",
  "team_elite",
];

/**
 * Pro+ (Pro, Studio, and legacy equivalents).
 */
const PRO_SOLO_PLUS: SubscriptionTier[] = [
  "pro",
  "studio",
  "pro_plus",
  "elite",
  "team_pro_plus",
  "team_elite",
];

/**
 * Studio only (and legacy team_elite).
 */
const STUDIO_ONLY: SubscriptionTier[] = [
  "studio",
  "team_elite",
];

export const FEATURE_GATES: Record<string, FeatureGateDefinition> = {
  // -------------------------------------------------------------------------
  // Core — all tiers (Starter and above)
  // -------------------------------------------------------------------------
  client_crm: { tier: ALL_TIERS },
  workout_builder: { tier: ALL_TIERS },
  scheduling: { tier: ALL_TIERS },
  messaging: { tier: ALL_TIERS },
  client_portal: { tier: ALL_TIERS },

  // -------------------------------------------------------------------------
  // Growth+ — AI, payments, analytics, SMS, groups, health, marketplace
  // -------------------------------------------------------------------------
  spotter_ai: { tier: GROWTH_PLUS },
  marketing_agent: { tier: GROWTH_PLUS },
  meal_planning_ai: { tier: GROWTH_PLUS },
  stripe_connect: { tier: GROWTH_PLUS },
  earnings_tracking: { tier: GROWTH_PLUS },
  pulse_check: { tier: GROWTH_PLUS },
  financial_intelligence: { tier: GROWTH_PLUS },
  forecast: { tier: GROWTH_PLUS },
  sms_reminders: { tier: GROWTH_PLUS },
  group_classes: { tier: GROWTH_PLUS },
  health_hub: { tier: GROWTH_PLUS },
  marketplace_listing: { tier: PRO_SOLO_PLUS, addon: "marketplace_listing" },
  marketing_suite: { tier: GROWTH_PLUS },
  referrals: { tier: GROWTH_PLUS },

  // -------------------------------------------------------------------------
  // Pro+ — white-label, custom branded app
  // -------------------------------------------------------------------------
  custom_branded_app: { tier: PRO_SOLO_PLUS },
  custom_subdomain: { tier: PRO_SOLO_PLUS },

  // -------------------------------------------------------------------------
  // Studio only — team seats, multi-location, API, dedicated support
  // -------------------------------------------------------------------------
  team_seats: { tier: STUDIO_ONLY },
  multi_location: { tier: STUDIO_ONLY },
  api_access: { tier: STUDIO_ONLY },
  company_dashboard: { tier: STUDIO_ONLY },

  // -------------------------------------------------------------------------
  // Addon-only features (purchasable on any tier)
  // -------------------------------------------------------------------------
  route_optimization: { addon: "route_optimization" },
  advanced_automations: { addon: "advanced_automations" },

  // -------------------------------------------------------------------------
  // Requires BOTH tier and addon
  // -------------------------------------------------------------------------
  whitelabel_app_store: {
    tier: STUDIO_ONLY,
    addon: "whitelabel_app_store",
    both: true,
  },
};

// ---------------------------------------------------------------------------
// Human-readable tier metadata (for UpgradeCard, settings screens, etc.)
// ---------------------------------------------------------------------------

export const TIER_DISPLAY_NAMES: Record<string, string> = {
  starter: "Starter",
  growth: "Growth",
  pro: "Pro",
  studio: "Studio",
};

/**
 * Look up the minimum-required tier name for a given feature key. This is
 * used by `UpgradeCard` to show "Upgrade to Growth to unlock …".
 */
export function getRequiredTierName(featureKey: string): string | null {
  const gate = FEATURE_GATES[featureKey];
  if (!gate || !gate.tier) return null;

  // Prefer the first new-pricing tier listed in the gate definition.
  const preferredOrder: SubscriptionTier[] = ["starter", "growth", "pro", "studio"];
  for (const tier of preferredOrder) {
    if (gate.tier.includes(tier)) {
      return TIER_DISPLAY_NAMES[tier] ?? null;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Addon catalog (for Settings UI + future Stripe product mapping)
// ---------------------------------------------------------------------------

export interface AddonDefinition {
  key: AddonKey;
  name: string;
  description: string;
  priceCents: number;
  /** If set, this addon requires a specific tier to be purchasable. */
  requiresTier?: SubscriptionTier[];
  /**
   * Whether this add-on can actually be billed/activated today.
   * Stripe subscription-item wiring is NOT live yet, so every add-on is
   * currently `false`. Until that lands, the Settings UI must not let a
   * trainer "activate" an add-on (it would imply billing that never happens).
   * Flip an entry to `true` only once its Stripe price + checkout path exist.
   */
  billable?: boolean;
}

export const ADDON_CATALOG: AddonDefinition[] = [
  {
    key: "route_optimization",
    name: "Route Optimization",
    description: "Plan the most efficient route across client visits.",
    priceCents: 900, // $9/mo
  },
  {
    key: "advanced_automations",
    name: "Advanced Automations",
    description: "Multi-step workflows, conditional triggers, and schedules.",
    priceCents: 1900, // $19/mo
  },
  {
    key: "whitelabel_app_store",
    name: "White-label App Store",
    description: "Ship your own branded client app to the App Store & Play Store.",
    priceCents: 7900, // $79/mo
    requiresTier: ["studio"],
  },
  {
    key: "marketplace_listing",
    name: "Marketplace listing",
    description: "Get discovered. Public profile in the TrainerPulse directory.",
    priceCents: 1500, // $15/mo
  },
];

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseFeatureAccessResult {
  tier: SubscriptionTier | null;
  subscriptionId: string | null;
  addons: ActiveAddon[];
  hasFeature: (featureKey: string) => boolean;
  hasAddon: (addonKey: AddonKey) => boolean;
  isLoading: boolean;
}

export function useFeatureAccess(): UseFeatureAccessResult {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const query = useQuery({
    queryKey: ["feature-access", userId],
    enabled: !!userId,
    queryFn: async (): Promise<{
      tier: SubscriptionTier | null;
      subscriptionId: string | null;
      addons: ActiveAddon[];
    }> => {
      if (!userId) return { tier: null, subscriptionId: null, addons: [] };

      const [subResult, addonsResult] = await Promise.all([
        supabase
          .from("subscriptions")
          .select("id, tier")
          .eq("user_id", userId)
          .maybeSingle(),
        // get_active_addons is a new RPC and may not be in the generated types
        // yet — cast to satisfy the current typed RPC signature list.
        supabase.rpc(
          "get_active_addons" as never,
          { p_user_id: userId } as never,
        ),
      ]);

      const tier = (subResult.data?.tier as SubscriptionTier | undefined) ?? null;
      const subscriptionId = (subResult.data?.id as string | undefined) ?? null;

      const rawAddons = (addonsResult.data ?? []) as unknown as ActiveAddon[];
      return { tier, subscriptionId, addons: rawAddons };
    },
  });

  const tier = query.data?.tier ?? null;
  const subscriptionId = query.data?.subscriptionId ?? null;
  const addons = query.data?.addons ?? [];

  const hasAddon = (key: AddonKey) => addons.some((a) => a.addon_key === key);

  const hasFeature = (featureKey: string): boolean => {
    const gate = FEATURE_GATES[featureKey];
    if (!gate) return false;

    const tierOk = gate.tier ? (tier !== null && gate.tier.includes(tier)) : false;
    const addonOk = gate.addon ? hasAddon(gate.addon) : false;

    if (gate.both) {
      return tierOk && addonOk;
    }

    if (gate.tier && gate.addon) return tierOk || addonOk;
    if (gate.tier) return tierOk;
    if (gate.addon) return addonOk;

    return false;
  };

  return {
    tier,
    subscriptionId,
    addons,
    hasFeature,
    hasAddon,
    isLoading: query.isLoading,
  };
}

// ---------------------------------------------------------------------------
// <FeatureGate> component
// ---------------------------------------------------------------------------

export interface FeatureGateProps {
  feature: string;
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Declaratively render `children` only if the current user has the feature.
 *
 * Example:
 *   <FeatureGate feature="route_optimization" fallback={<UpgradePrompt />}>
 *     <RouteOptimizer />
 *   </FeatureGate>
 */
export function FeatureGate({ feature, fallback = null, children }: FeatureGateProps) {
  const { hasFeature, isLoading } = useFeatureAccess();

  if (isLoading) return null;
  if (!hasFeature(feature)) return <>{fallback}</>;

  return <>{children}</>;
}
